import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { chatService } from '../services/chat.service';
import { QUERY_KEYS } from '../constants';
import type { ApiResponse } from '../types/common';
import type {
  StartChatRequest,
  StartChatResponse,
  SendMessageRequest,
  SendMessageResponse,
  GetChatParams,
  GetChatResponse,
  GetUserChatsResponse,
} from '../types/chat.types';

/**
 * Hook to start a new chat with a character
 * Creates a new chat or returns existing one
 */
export const useStartChat = (): UseMutationResult<
  ApiResponse<StartChatResponse>,
  Error,
  StartChatRequest
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: StartChatRequest) => chatService.startChat(request),
    onSuccess: () => {
      // Invalidate user chats list to show the new/updated chat
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CHAT.USER_CHATS });
    },
  });
};

/**
 * Hook to send a message to a chat
 * Consumes 1 message credit per request
 */
export const useSendMessage = (): UseMutationResult<
  ApiResponse<SendMessageResponse>,
  Error,
  SendMessageRequest
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: SendMessageRequest) => chatService.sendMessage(request),
    onSuccess: (_data, variables) => {
      // Note: CHAT.DETAIL is invalidated by ChatPage after typewriter completes
      // so we can show the AI response with typewriter animation first
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.AUTH.USER_PROFILE,
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.CHAT.USER_CHATS,
      });
    },
  });
};

/**
 * Hook to fetch a specific chat with all messages
 * @param chatId - The chat ID to fetch
 * @param enabled - Whether to enable the query (default: true)
 */
export const useChat = (
  chatId: string | undefined,
  enabled: boolean = true
): UseQueryResult<ApiResponse<GetChatResponse>, Error> => {
  return useQuery({
    queryKey: QUERY_KEYS.CHAT.DETAIL(chatId ?? ''),
    queryFn: () => chatService.getChat({ chatId: chatId! }),
    enabled: enabled && !!chatId, // Only run if chatId exists and enabled
    staleTime: 1000 * 30, // 30 seconds
  });
};

/**
 * Hook to fetch all chats for the authenticated user
 */
export const useUserChats = (): UseQueryResult<ApiResponse<GetUserChatsResponse>, Error> => {
  return useQuery({
    queryKey: QUERY_KEYS.CHAT.USER_CHATS,
    queryFn: () => chatService.getUserChats(),
    staleTime: 1000 * 60, // 1 minute
  });
};
